/** Shapes of the affiliate admin operations (packages/affiliate/src/operations/admin). */

export type ParameterLevel = 'affiliate_network' | 'site' | 'global' | 'global_country' | 'shop' | 'link' | 'fallback_url';
export type ParameterType = 'url' | 'query' | 'bearer' | 'other' | 'click_ref';
export const PARAMETER_TYPES: ParameterType[] = ['url', 'query', 'click_ref', 'bearer', 'other'];

export interface Named {
  ref: string;
  name: string;
}

export interface NetworkRow extends Named {
  slug: string;
  kind: 'affiliate_network' | 'sub_affiliate_network';
  status: 'active' | 'inactive';
  homepageUrl: string | null;
  generatorKey: string | null;
  generatorLabel: string | null;
  clickoutAdapter: string;
  countries: string[];
  clickRefParameter: string | null;
  validateSubid: boolean;
  validationRegex: string | null;
  approvalLevel: ApprovalLevel;
  subAffiliatePolicy: SubAffiliatePolicy;
  accounts: number;
  parameters: number;
  programs: number;
  approvedPrograms: number;
  sitesWithoutAdSpace: Named[];
}

export interface NetworkList {
  rows: NetworkRow[];
  generators: { key: string; label: string }[];
  clickoutAdapters: string[];
  countries: { code: string; name: string }[];
  canManage: boolean;
}

export type ApprovalLevel = 'account' | 'ad_space' | 'none';
export type SubAffiliatePolicy = 'allowed' | 'declare_sub_publisher' | 'forbidden';
export type FillSource = 'account' | 'ad_space';

export const APPROVAL_LEVEL_LABELS: Record<ApprovalLevel, string> = {
  account: 'Per account (new sites inherit approvals)',
  ad_space: 'Per ad space (each site applies)',
  none: 'No approvals (tracking tags)',
};

export const SUB_AFFILIATE_LABELS: Record<SubAffiliatePolicy, string> = {
  allowed: 'Allowed on partner sites',
  declare_sub_publisher: 'Partner sites as declared sub-publishers',
  forbidden: 'Not on partner sites (partner uses its own account)',
};

export interface NetworkAccountRow extends Named {
  network: Named & { slug: string; approvalLevel: ApprovalLevel };
  owner: Named & { isOperator: boolean };
  legalEntity: string | null;
  businessModel: 'voucher' | 'content' | 'cashback' | 'other';
  payoutCurrency: string;
  region: string | null;
  externalAccountId: string | null;
  accessMode: 'owned' | 'user_access' | 'partner_managed';
  credentialRefs: Record<string, string>;
  status: 'active' | 'paused' | 'closed';
  notes: string | null;
  adSpaces: number;
  approvedPrograms: number;
  memberships: number;
  canManage: boolean;
}

export interface MissingAdSpace {
  site: Named;
  network: Named;
  reason: 'none' | 'empty' | 'inactive';
  merchants: number;
}

export interface NetworkAccountList {
  rows: NetworkAccountRow[];
  missing: MissingAdSpace[];
  networks: (Named & { slug: string; approvalLevel: ApprovalLevel; subAffiliatePolicy: SubAffiliatePolicy })[];
  owners: (Named & { isOperator: boolean })[];
  canCreate: boolean;
}

export interface NetworkAccountDetail {
  account: NetworkAccountRow;
  adSpaces: { site: Named & { organization: string }; ids: string; status: string; warnings: string[]; errors: string[]; canManage: boolean }[];
  missingSites: (Named & { reason: 'none' | 'empty' | 'inactive'; merchants: number })[];
  memberships: { id: number; program: Named; site: Named | null; relationshipStatus: Relationship; approvedAt: string | null }[];
}

export interface ProgramMemberships {
  approvalLevel: ApprovalLevel;
  memberships: {
    id: number;
    account: Named & { owner: string };
    site: Named | null;
    relationshipStatus: Relationship;
    approvedAt: string | null;
    notes: string | null;
    canManage: boolean;
    currentTerms: { id: number; terms: Record<string, unknown>; validFrom: string; validTo: string | null; source: string; negotiatedBy: string | null } | null;
    termsHistory: number;
  }[];
  accounts: (Named & { owner: string; canManage: boolean; sites: Named[] })[];
}

export type Relationship = 'approved' | 'pending' | 'not_joined' | 'declined' | 'unknown';
export const RELATIONSHIPS: Relationship[] = ['approved', 'pending', 'not_joined', 'declined', 'unknown'];

export interface Commission {
  type?: 'percent' | 'fixed' | 'mixed';
  default?: number;
  min?: number;
  max?: number;
  currency?: string;
  notes?: string;
}

export interface ProgramRow extends Named {
  network: Named;
  networkProgramId: string;
  homepage: string | null;
  trackingUrl: string | null;
  primaryCountry: string | null;
  shipToCountries: string[];
  status: 'active' | 'deactivated' | 'deleted';
  relationshipStatus: Relationship;
  deeplinkAllowed: boolean | null;
  commission: Commission;
  commissionLabel: string | null;
  networkNotes: string | null;
  mappingState: 'unmapped' | 'suggested' | 'confirmed';
  master: (Named & { externalIds: Record<string, string> }) | null;
  externalIds: Record<string, string>;
  merchants: { ref: string; name: string; country: string; active: boolean; activeProgramRef: string | null }[];
}

export interface ProgramList {
  total: number;
  rows: ProgramRow[];
  networks: Named[];
  countries: string[];
  canManage: boolean;
}

export interface ValueScope {
  site: Named | null;
  merchant: Named | null;
  master: Named | null;
  country: string | null;
  label: string;
}

export interface DeeplinkParameter {
  id: number;
  position: number;
  parameter: string;
  parameterType: ParameterType;
  level: ParameterLevel;
  levelLabel: string;
  isOptional: boolean;
  fallbackValue: string | null;
  fromProgram: boolean;
  fillFrom: FillSource | null;
  values: number;
}

export interface NetworkDeeplinks {
  network: { ref: string; name: string; generatorKey: string | null; generatorLabel: string | null };
  parameters: DeeplinkParameter[];
  values: { id: number; parameterId: number; value: string; scope: ValueScope; editable: boolean }[];
  sites: { ref: string; name: string; country: string; canManage: boolean; adSpace: string | null }[];
  levels: { level: ParameterLevel; label: string }[];
  canManage: boolean;
}

export type Origin = 'value' | 'program' | 'account' | 'ad_space' | 'extracted' | 'fallback' | 'deep_url' | 'fallback_url' | 'click_ref' | 'missing' | 'per_merchant';

export interface ValueMatrix {
  parameters: { id: number; parameter: string; level: ParameterLevel; levelLabel: string; parameterType: ParameterType; fromProgram: boolean; fillFrom: FillSource | null }[];
  merchant: Named | null;
  program: { ref: string; name: string; networkProgramId: string } | null;
  rows: {
    site: Named;
    merchantOnSite: boolean;
    adSpace: string | null;
    ready: boolean;
    cells: { parameterId: number; value: string | null; origin: Origin; level: ParameterLevel; valueId: number | null; blocking: boolean }[];
  }[];
}

export interface AdSpaceList {
  networks: { ref: string; name: string; siteParameter: string | null }[];
  sites: { ref: string; name: string; country: string; canManage: boolean }[];
  cells: { networkRef: string; siteRef: string; adSpaces: string | null; siteValue: string | null }[];
}

export interface DeeplinkTest {
  site: Named;
  network: Named;
  program: { ref: string; name: string; relationshipStatus: string; deeplinkAllowed: boolean | null } | null;
  merchant: Named | null;
  generator: { key: string | null; label: string | null; callsApi: boolean };
  deepUrl: string | null;
  fallbackUrl: string | null;
  ok: boolean;
  generated: boolean;
  trackingUrl: string | null;
  error: string | null;
  clickRefPlaceholder: string;
  parameters: { parameter: string; parameterType: ParameterType; level: ParameterLevel; levelLabel: string; value: string | null; origin: string; requiredMissing: boolean; scope: ValueScope | null }[];
  warnings: string[];
}

export type PartRole = 'base' | 'ad_space' | 'merchant_id' | 'campaign' | 'click_ref' | 'target_url' | 'static' | 'unknown';

export interface LinkAnalysis {
  family: string | null;
  familyLabel: string | null;
  suggestedGenerator: { key: string; label: string };
  currentGenerator: string | null;
  host: string;
  pathTemplate: string;
  targetUrls: string[];
  parts: { name: string; location: string; role: PartRole; samples: string[]; varies: boolean; level: ParameterLevel; levelLabel: string; reason: string }[];
  parameters: { position: number; parameter: string; parameterType: ParameterType; level: ParameterLevel; levelLabel: string; isOptional: boolean; fromProgram: boolean; role: PartRole; existing: 'new' | 'same' | 'different'; existingLevel: ParameterLevel | null }[];
  values: { parameter: string; value: string; siteRef: string | null; merchantRef: string | null; level: ParameterLevel; scopeLabel: string; current: string | null }[];
  programMatches: { parameter: string; value: string; program: { ref: string; name: string; merchant: string | null } | null }[];
  warnings: string[];
}

export interface LinkRow {
  id: number;
  network: Named;
  programRef: string | null;
  site: Named | null;
  isActive: boolean;
  status: string;
  url: string | null;
  deepUrl: string | null;
  error: string | null;
  generatedAt: string | null;
  source: string;
}

export interface MerchantLinks {
  merchant: { ref: string; name: string; activeProgramRef: string | null };
  programs: { ref: string; name: string; network: Named; active: boolean; relationshipStatus: string }[];
  sites: Named[];
  coupons: { ref: string; title: string; kind: string; endsAt: string | null; links: LinkRow[] }[];
  merchantLinks: LinkRow[];
  canChange: boolean;
}

export interface RegeneratePreview {
  program: string;
  network: string;
  summary: { new: number; changed: number; same: number; fails: number };
  lines: { kind: 'coupon' | 'merchant'; couponRef: string | null; couponTitle: string | null; site: Named | null; currentUrl: string | null; newUrl: string | null; change: 'new' | 'changed' | 'same' | 'fails'; error: string | null }[];
  allowed: boolean;
}

export const ROLE_LABELS: Record<PartRole, string> = {
  base: 'Base URL',
  ad_space: 'Ad space (your site)',
  merchant_id: 'Merchant / program id',
  campaign: 'Campaign id',
  click_ref: 'Click reference',
  target_url: 'Target URL',
  static: 'Fixed',
  unknown: 'Unknown',
};

export const ORIGIN_LABELS: Record<Origin | string, string> = {
  value: 'stored value',
  program: 'program id',
  account: 'account id',
  ad_space: 'ad space id',
  extracted: 'read from the program’s tracking link',
  fallback: 'parameter default',
  deep_url: 'deep URL of the link',
  fallback_url: 'merchant fallback URL',
  click_ref: 'click id at clickout',
  missing: 'missing',
  per_merchant: 'per merchant',
};

export interface DeeplinkCoverage {
  fromProgramParameters: string[];
  sites: {
    site: Named & { country: string };
    merchants: number;
    canGenerate: number;
    missingMerchantId: number;
    missingAdSpace: boolean;
    missing: (Named & { parameters: string[] })[];
  }[];
}
